// src/pages/Game/GameTimeSetting.jsx
import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import { toast } from 'react-toastify';

const GameTimeSetting = () => {
  const [loading, setLoading] = useState(false);
  const [gameMode, setGameMode] = useState('24h');
  const [displayFormat, setDisplayFormat] = useState('12');
  const [startTime, setStartTime] = useState('10:00');
  const [endTime, setEndTime] = useState('22:00');
  const [currentStatus, setCurrentStatus] = useState(null);
  
  // For time selection in 12-hour format - using separate hour/minute and period
  const [startHour, setStartHour] = useState('10');
  const [startMinute, setStartMinute] = useState('00');
  const [startPeriod, setStartPeriod] = useState('AM');
  
  const [endHour, setEndHour] = useState('10');
  const [endMinute, setEndMinute] = useState('00');
  const [endPeriod, setEndPeriod] = useState('PM');

  useEffect(() => {
    fetchGameSettings();
    fetchGameStatus();
  }, []);

  // Update 24-hour time when 12-hour inputs change
  useEffect(() => {
    if (displayFormat === '12') {
      const time24 = convertTo24Hour(startHour, startMinute, startPeriod);
      setStartTime(time24);
      
      if (gameMode === '12h') {
        updateEndTimeFromStart(time24);
      }
    }
  }, [startHour, startMinute, startPeriod]);

  // Convert 12-hour to 24-hour
  const convertTo24Hour = (hour, minute, period) => {
    let h = parseInt(hour);
    
    if (period === 'PM' && h !== 12) {
      h += 12;
    } else if (period === 'AM' && h === 12) {
      h = 0;
    }
    
    return `${h.toString().padStart(2, '0')}:${minute}`;
  };

  // Convert 24-hour to 12-hour components
  const convertTo12HourComponents = (time24) => {
    if (!time24) return { hour: '12', minute: '00', period: 'AM' };
    const [hours, minutes] = time24.split(':');
    const hour = parseInt(hours);
    const period = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12;
    return {
      hour: hour12.toString().padStart(2, '0'),
      minute: minutes,
      period: period
    };
  };

  // Calculate end time exactly 12 hours after start time
  const calculateEndTime = (startTime24) => {
    const [hours, minutes] = startTime24.split(':');
    let totalMinutes = parseInt(hours) * 60 + parseInt(minutes);
    
    // Add 12 hours (720 minutes)
    totalMinutes += 720;
    
    // Handle wrap around (next day)
    if (totalMinutes >= 24 * 60) {
      totalMinutes -= 24 * 60;
    }
    
    const endHours = Math.floor(totalMinutes / 60);
    const endMinutes = totalMinutes % 60;
    
    return `${endHours.toString().padStart(2, '0')}:${endMinutes.toString().padStart(2, '0')}`;
  };

  // Update end time based on start time
  const updateEndTimeFromStart = (newStartTime24) => {
    const newEndTime24 = calculateEndTime(newStartTime24);
    setEndTime(newEndTime24);
    
    // Update 12-hour format display
    const end12 = convertTo12HourComponents(newEndTime24);
    setEndHour(end12.hour);
    setEndMinute(end12.minute);
    setEndPeriod(end12.period);
  };

  const fetchGameSettings = async () => {
    try {
      const response = await api.get('/admin/game/settings');
      if (response.data && response.data.success) {
        setGameMode(response.data.gameTimeMode || '24h');
        setDisplayFormat(response.data.displayFormat || '12');
        
        const startTime24 = response.data.gameActiveStartTime || '10:00';
        const endTime24 = response.data.gameActiveEndTime || '22:00';
        
        setStartTime(startTime24);
        setEndTime(endTime24);
        
        // For display in 12-hour format
        const start12 = convertTo12HourComponents(startTime24);
        const end12 = convertTo12HourComponents(endTime24);
        
        setStartHour(start12.hour);
        setStartMinute(start12.minute);
        setStartPeriod(start12.period);
        setEndHour(end12.hour);
        setEndMinute(end12.minute);
        setEndPeriod(end12.period);
      }
    } catch (error) {
      console.error('Error fetching game settings:', error);
      toast.error('Failed to load game settings');
    }
  };

  const fetchGameStatus = async () => {
    try {
      const response = await api.get('/admin/game/status');
      if (response.data && response.data.success) {
        setCurrentStatus(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching game status:', error);
    }
  };

  const handleModeChange = (mode) => {
    setGameMode(mode);
    if (mode === '24h') {
      toast.info('24-hour mode: Game will run continuously');
    } else {
      toast.info('12-hour mode: Game will run for 12 hours, then pause for 12 hours');
      // When switching to 12h mode, ensure end time is exactly 12 hours after start
      updateEndTimeFromStart(startTime);
    }
  };

  const handleStartHourChange = (e) => {
    setStartHour(e.target.value);
  };

  const handleStartMinuteChange = (e) => {
    setStartMinute(e.target.value);
  };

  const handleStartPeriodChange = (e) => {
    const newPeriod = e.target.value;
    setStartPeriod(newPeriod);
  };

  const handleEndTimeChange = (e) => {
    // In 12-hour mode, end time is locked
    if (gameMode === '12h') {
      toast.warning('In 12-hour mode, end time is automatically set to 12 hours after start time');
      return;
    }
    
    if (displayFormat === '12') {
      setEndHour(e.target.value);
      // Update 24-hour time
      const time24 = convertTo24Hour(endHour, endMinute, endPeriod);
      setEndTime(time24);
    }
  };

  const handleEndMinuteChange = (e) => {
    if (gameMode === '12h') {
      toast.warning('In 12-hour mode, end time is automatically set to 12 hours after start time');
      return;
    }
    setEndMinute(e.target.value);
    const time24 = convertTo24Hour(endHour, e.target.value, endPeriod);
    setEndTime(time24);
  };

  const handleEndPeriodChange = (e) => {
    if (gameMode === '12h') {
      toast.warning('In 12-hour mode, end time is automatically set to 12 hours after start time');
      return;
    }
    setEndPeriod(e.target.value);
    const time24 = convertTo24Hour(endHour, endMinute, e.target.value);
    setEndTime(time24);
  };

  const handleDisplayFormatChange = (format) => {
    setDisplayFormat(format);
  };

  const validateTimePeriod = () => {
    const [startHours, startMinutes] = startTime.split(':');
    const [endHours, endMinutes] = endTime.split(':');
    
    let startTotal = parseInt(startHours) * 60 + parseInt(startMinutes);
    let endTotal = parseInt(endHours) * 60 + parseInt(endMinutes);
    
    let duration = endTotal - startTotal;
    if (duration < 0) {
      duration += 24 * 60;
    }
    
    return duration === 12 * 60;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (gameMode === '12h') {
      if (!startTime) {
        toast.error('Please select start time');
        return;
      }
      
      // Validate that end time is exactly 12 hours after start
      if (!validateTimePeriod()) {
        // Auto-correct the end time
        const correctedEndTime = calculateEndTime(startTime);
        setEndTime(correctedEndTime);
        const end12 = convertTo12HourComponents(correctedEndTime);
        setEndHour(end12.hour);
        setEndMinute(end12.minute);
        setEndPeriod(end12.period);
        toast.info(`End time automatically set to ${formatDisplayTime(correctedEndTime)} (12 hours later)`);
      }
    }
    
    setLoading(true);
    try {
      const payload = {
        gameTimeMode: gameMode,
        displayFormat: displayFormat
      };
      
      if (gameMode === '12h') {
        payload.gameActiveStartTime = startTime;
        payload.gameActiveEndTime = endTime;
      }
      
      const response = await api.put('/admin/game/settings', payload);
      
      if (response.data.success) {
        toast.success(response.data.message);
        fetchGameStatus();
      } else {
        toast.error('Failed to save game settings');
      }
    } catch (error) {
      console.error('Error saving game settings:', error);
      toast.error(error.response?.data?.error || 'Failed to save game settings');
    } finally {
      setLoading(false);
    }
  };

  const formatDisplayTime = (time24) => {
    if (!time24) return '--:--';
    if (displayFormat === '12') {
      const { hour, minute, period } = convertTo12HourComponents(time24);
      return `${hour}:${minute} ${period}`;
    }
    return time24;
  };

  const isEndTimeLocked = gameMode === '12h';

  // Generate hour options (1-12)
  const hourOptions = Array.from({ length: 12 }, (_, i) => (i + 1).toString().padStart(2, '0'));
  
  // Generate minute options (00-59)
  const minuteOptions = Array.from({ length: 60 }, (_, i) => i.toString().padStart(2, '0'));

  return (
    <div className="time-setting-page">
      <div className="container-fluid">
        <div className="page-hedding-header">
          <h1 className="page-heading h3 text-uppercase fs-4">Game Time Setting</h1>
        </div>
        
        <div className="admin-card-one">
          <div className="admin-card-header">
            <h2 className="admin-card-heading">Configure Game Schedule</h2>
          </div>
          <div className="admin-card-body">
            <form onSubmit={handleSubmit}>
              {/* Display Format Selection */}
              <div className="row g-3 mb-4">
                <div className="col-lg-6">
                  <label className="admin-label-one">Display Format</label>
                  <div className="d-flex gap-3">
                    <div className="form-check">
                      <input
                        type="radio"
                        id="format12"
                        name="displayFormat"
                        value="12"
                        checked={displayFormat === '12'}
                        onChange={() => handleDisplayFormatChange('12')}
                        className="form-check-input"
                      />
                      <label htmlFor="format12" className="form-check-label">
                        12-Hour Format (AM/PM)
                      </label>
                    </div>
                    <div className="form-check">
                      <input
                        type="radio"
                        id="format24"
                        name="displayFormat"
                        value="24"
                        checked={displayFormat === '24'}
                        onChange={() => handleDisplayFormatChange('24')}
                        className="form-check-input"
                      />
                      <label htmlFor="format24" className="form-check-label">
                        24-Hour Format
                      </label>
                    </div>
                  </div>
                  <small className="text-muted">
                    How times should be displayed in the interface
                  </small>
                </div>
              </div>

              {/* Game Mode Selection */}
              <div className="row g-3 mb-4">
                <div className="col-lg-12">
                  <label className="admin-label-one">Game Mode</label>
                  <div className="d-flex gap-3">
                    <div className="form-check">
                      <input
                        type="radio"
                        id="mode24h"
                        name="gameMode"
                        value="24h"
                        checked={gameMode === '24h'}
                        onChange={() => handleModeChange('24h')}
                        className="form-check-input"
                      />
                      <label htmlFor="mode24h" className="form-check-label">
                        24-Hour Mode (Continuous)
                      </label>
                    </div>
                    <div className="form-check">
                      <input
                        type="radio"
                        id="mode12h"
                        name="gameMode"
                        value="12h"
                        checked={gameMode === '12h'}
                        onChange={() => handleModeChange('12h')}
                        className="form-check-input"
                      />
                      <label htmlFor="mode12h" className="form-check-label">
                        12-Hour Mode (12 hours active, 12 hours inactive)
                      </label>
                    </div>
                  </div>
                </div>
              </div>

              {/* Time Selection */}
              <div className="row g-3 mb-4">
                <div className="col-lg-6">
                  <label className="admin-label-one">Active Period Start Time</label>
                  {displayFormat === '12' ? (
                    <div className="row g-2">
                      <div className="col-4">
                        <select
                          value={startHour}
                          onChange={handleStartHourChange}
                          className="form-select admin-select-one"
                          required
                        >
                          {hourOptions.map(hour => (
                            <option key={hour} value={hour}>{hour}</option>
                          ))}
                        </select>
                      </div>
                      <div className="col-4">
                        <select
                          value={startMinute}
                          onChange={handleStartMinuteChange}
                          className="form-select admin-select-one"
                          required
                        >
                          {minuteOptions.map(minute => (
                            <option key={minute} value={minute}>{minute}</option>
                          ))}
                        </select>
                      </div>
                      <div className="col-4">
                        <select
                          value={startPeriod}
                          onChange={handleStartPeriodChange}
                          className="form-select admin-select-one"
                        >
                          <option value="AM">AM</option>
                          <option value="PM">PM</option>
                        </select>
                      </div>
                    </div>
                  ) : (
                    <input
                      type="time"
                      value={startTime}
                      onChange={(e) => setStartTime(e.target.value)}
                      className="form-control admin-input-one"
                      required
                    />
                  )}
                  <small className="text-muted">
                    Game will start at this time
                  </small>
                </div>
                
                <div className="col-lg-6">
                  <label className="admin-label-one">
                    Active Period End Time
                    {/* {isEndTimeLocked && (
                      <span className="badge bg-info ms-2" style={{ fontSize: '10px' }}>
                        Auto-calculated
                      </span>
                    )} */}
                  </label>
                  {displayFormat === '12' ? (
                    <div className="row g-2">
                      <div className="col-4">
                        <select
                          value={endHour}
                          onChange={handleEndTimeChange}
                          className="form-select admin-select-one"
                          disabled={isEndTimeLocked}
                          style={isEndTimeLocked ? { backgroundColor: '#f0f0f0' } : {}}
                          required
                        >
                          {hourOptions.map(hour => (
                            <option key={hour} value={hour}>{hour}</option>
                          ))}
                        </select>
                      </div>
                      <div className="col-4">
                        <select
                          value={endMinute}
                          onChange={handleEndMinuteChange}
                          className="form-select admin-select-one"
                          disabled={isEndTimeLocked}
                          style={isEndTimeLocked ? { backgroundColor: '#f0f0f0' } : {}}
                          required
                        >
                          {minuteOptions.map(minute => (
                            <option key={minute} value={minute}>{minute}</option>
                          ))}
                        </select>
                      </div>
                      <div className="col-4">
                        <select
                          value={endPeriod}
                          onChange={handleEndPeriodChange}
                          className="form-select admin-select-one"
                          disabled={isEndTimeLocked}
                          style={isEndTimeLocked ? { backgroundColor: '#f0f0f0' } : {}}
                        >
                          <option value="AM">AM</option>
                          <option value="PM">PM</option>
                        </select>
                      </div>
                    </div>
                  ) : (
                    <input
                      type="time"
                      value={endTime}
                      onChange={(e) => setEndTime(e.target.value)}
                      className="form-control admin-input-one"
                      disabled={isEndTimeLocked}
                      style={isEndTimeLocked ? { backgroundColor: '#f0f0f0' } : {}}
                      required
                    />
                  )}
                  <small className="text-muted">
                    {isEndTimeLocked 
                      ? `Automatically set to 12 hours after start time (${formatDisplayTime(endTime)})`
                      : 'Game will end at this time'}
                  </small>
                </div>
              </div>

              {/* Submit Button */}
              <div className="row g-3">
                <div className="col-lg-12">
                  <button 
                    type="submit" 
                    className="admin-submit-one" 
                    disabled={loading}
                  >
                    {loading ? 'Saving...' : 'Save Game Schedule'}
                  </button>
                </div>
              </div>
            </form>

         
            {/* Game Status Display */}
            {currentStatus && (
              <div className={`alert mt-4 ${currentStatus.isGameActive ? 'alert-success' : 'alert-warning'}`}>
                <h5 className="alert-heading">
                  {currentStatus.isGameActive ? '🟢 Game is Active' : '🔴 Game is Inactive'}
                </h5>
                <p className="mb-0">{currentStatus.message}</p>
                {currentStatus.nextActiveTime && (
                  <small>
                    Next active period starts: {new Date(currentStatus.nextActiveTime).toLocaleString()}
                  </small>
                )}
                <hr />
                <small className="d-block">
                  Server Time: {currentStatus.currentTimeFormatted}
                </small>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default GameTimeSetting;